/**
 * Typed turn-error taxonomy for the chat turn engine (HIG-5969, design:
 * docs/research/chat-engine-design-no-effect.md §1).
 *
 * Pure TS — no React imports. Errors are plain data discriminated on `_tag`;
 * engine code never throws across this boundary. I/O sites wrap their
 * promises with the neverthrow helpers below so the error channel is in the
 * signature, and `toChatTurnError` normalizes anything caught from the
 * legacy throw-based flow.
 */
import { Result, ResultAsync } from 'neverthrow'
import type { ChallengeFailureReason } from '@shared/challenge/types'

export type TurnPhase = 'submit' | 'resume'

/** The fetch for the turn's SSE stream (submit POST / subscribe GET) failed to connect. */
export type SseConnectionError = {
  readonly _tag: 'SseConnectionError'
  readonly phase: TurnPhase
  readonly chatId: string
  readonly cause: unknown
}

/** An SSE frame arrived but its JSON payload could not be parsed. */
export type SseDecodeError = {
  readonly _tag: 'SseDecodeError'
  readonly phase: TurnPhase
  readonly chatId: string
  readonly rawFrame: string
}

/**
 * The backend rejected or failed the turn.
 * - `error-response`: non-OK HTTP response with a well-formed error body
 * - `non-json-response`: non-OK HTTP response whose body was not JSON
 * - `malformed-error-body`: non-OK HTTP response whose JSON body had no string `error`
 * - `stream-error-event`: an `error` event arrived on the SSE stream
 * - `unsuccessful-completion`: the durable terminal receipt reports that the turn failed
 */
export type BackendTurnError = {
  readonly _tag: 'BackendTurnError'
  readonly phase: TurnPhase
  readonly chatId: string
  readonly kind:
    | 'error-response'
    | 'non-json-response'
    | 'malformed-error-body'
    | 'stream-error-event'
    | 'unsuccessful-completion'
  readonly message: string
  readonly errorCode: string | null
  readonly rayId: string
}

/** The Arkose captcha challenge could not be solved before submit. */
export type ArkoseChallengeError = {
  readonly _tag: 'ArkoseChallengeError'
  readonly chatId: string
  readonly reason: ChallengeFailureReason
  /** Diagnostic detail. `describeTurnError` branches on `reason` for user copy. */
  readonly message: string
}

/** Finalizing/uploading an attachment failed before the turn was submitted. */
export type AttachmentUploadError = {
  readonly _tag: 'AttachmentUploadError'
  readonly chatId: string
  readonly message: string
  readonly rayId: string
}

/**
 * A streaming-state verify response was deliberately dropped instead of
 * applied. Not a turn failure — a typed, logged replacement for the silent
 * guard returns in use-sync-streaming-state.
 */
export type StaleVerifyDropped = {
  readonly _tag: 'StaleVerifyDropped'
  readonly chatId: string
  readonly reason: 'fresh-submit-owns-chat' | 'live-turn-owns-chat'
}

/**
 * The backend refused the turn because the user's plan bills credits and the
 * balance is spent (402 / `CREDITS_EXHAUSTED`). Split out of `BackendTurnError`
 * because it is the one backend refusal the user can fix themselves: retrying
 * only re-spends a rate-limit token, so the tag is what suppresses the Retry
 * button, and what a top-up CTA would branch on. Free users never see it,
 * since their free-model chat is not credit-billed.
 */
export type CreditsExhausted = {
  readonly _tag: 'CreditsExhausted'
  readonly phase: TurnPhase
  readonly chatId: string
  readonly message: string
  readonly rayId: string
}

/**
 * The backend refused the turn because this 2.0 build has no subscription
 * behind it (402 / `SUBSCRIPTION_REQUIRED`). 2.0 has no free tier, so where a
 * 1.0 user would have been dropped to a free model, this user is sent to pick
 * a plan. Like `CreditsExhausted`, retrying cannot change the answer.
 */
export type SubscriptionRequired = {
  readonly _tag: 'SubscriptionRequired'
  readonly phase: TurnPhase
  readonly chatId: string
  readonly message: string
  readonly rayId: string
}

/**
 * The backend asked for a card before more work (402 / `CARD_REQUIRED`).
 * Adding a card lifts it, so the CTA opens a checkout that saves one. Retrying
 * before then cannot change the answer.
 */
export type CardRequired = {
  readonly _tag: 'CardRequired'
  readonly phase: TurnPhase
  readonly chatId: string
  readonly message: string
  readonly rayId: string
}

/**
 * The backend refused the turn because this account never recorded finishing
 * 2.0 onboarding (403 / `ONBOARDING_REQUIRED`), usually a 1.0 install that
 * carried its local "onboarded" flag into 2.0. The client sends the user back
 * through the flow. Retrying cannot change the answer.
 */
export type OnboardingRequired = {
  readonly _tag: 'OnboardingRequired'
  readonly phase: TurnPhase
  readonly chatId: string
  readonly message: string
  readonly rayId: string
}

/** Anything caught that no other member describes. */
export type UnknownTurnError = {
  readonly _tag: 'UnknownTurnError'
  readonly phase: TurnPhase
  readonly chatId: string
  readonly cause: unknown
}

export type ChatTurnError =
  | SseConnectionError
  | SseDecodeError
  | BackendTurnError
  | ArkoseChallengeError
  | AttachmentUploadError
  | CreditsExhausted
  | SubscriptionRequired
  | CardRequired
  | OnboardingRequired
  | StaleVerifyDropped
  | UnknownTurnError

/**
 * Why a turn was interrupted. `unsubscribed` is a client-initiated abort
 * (drawer close / view unmount — the backend may still be streaming);
 * `superseded` means a newer turn for the same chat took ownership via the
 * turn registry.
 */
export const TURN_INTERRUPT_REASONS = ['unsubscribed', 'superseded'] as const
export type TurnInterruptReason = (typeof TURN_INTERRUPT_REASONS)[number]

/** Interrupt reason carried by a turn's abort signal, defaulting to a client abort. */
export function interruptReasonFrom(signal: AbortSignal): TurnInterruptReason {
  const reason: unknown = signal.reason
  return typeof reason === 'string' && (TURN_INTERRUPT_REASONS as ReadonlyArray<string>).includes(reason)
    ? (reason as TurnInterruptReason)
    : 'unsubscribed'
}

/**
 * Terminal outcome of a turn on the Ok side of
 * `ResultAsync<TurnCompletion, ChatTurnError>`. Interruption (user abort,
 * superseding turn) is NOT an error and must never be modeled as one — engine
 * entry points resolve to this. `completed` carries the final accumulator
 * state (`TurnState` for the resume pipeline); the generic keeps this file
 * free of accumulator imports.
 */
export type TurnCompletion<State = unknown> =
  | { readonly _tag: 'completed'; readonly state: State }
  | { readonly _tag: 'interrupted'; readonly reason: TurnInterruptReason }

export type TurnErrorContext = { readonly phase: TurnPhase; readonly chatId: string }

/** Sentinel rayId for failures with no HTTP response to read one from. */
export const RAY_ID_UNAVAILABLE = 'Not available'

/** Cloudflare ray id from a response's headers, or the sentinel. */
export const rayIdFromHeaders = (headers: Headers): string => {
  return headers.get('cf-ray') ?? headers.get('Cf-Ray') ?? headers.get('CF-RAY') ?? RAY_ID_UNAVAILABLE
}

export const sseConnectionError = (ctx: TurnErrorContext, cause: unknown): SseConnectionError => ({
  _tag: 'SseConnectionError',
  phase: ctx.phase,
  chatId: ctx.chatId,
  cause,
})

export const sseDecodeError = (ctx: TurnErrorContext, rawFrame: string): SseDecodeError => ({
  _tag: 'SseDecodeError',
  phase: ctx.phase,
  chatId: ctx.chatId,
  rawFrame,
})

export const backendTurnError = (
  ctx: TurnErrorContext,
  details: { kind: BackendTurnError['kind']; message: string; errorCode?: string | null; rayId?: string },
): BackendTurnError => ({
  _tag: 'BackendTurnError',
  phase: ctx.phase,
  chatId: ctx.chatId,
  kind: details.kind,
  message: details.message,
  errorCode: details.errorCode ?? null,
  rayId: details.rayId ?? RAY_ID_UNAVAILABLE,
})

export const arkoseChallengeError = (
  chatId: string,
  reason: ChallengeFailureReason,
  message: string,
): ArkoseChallengeError => ({
  _tag: 'ArkoseChallengeError',
  chatId,
  reason,
  message,
})

export const attachmentUploadError = (chatId: string, message: string, rayId?: string): AttachmentUploadError => ({
  _tag: 'AttachmentUploadError',
  chatId,
  message,
  rayId: rayId ?? RAY_ID_UNAVAILABLE,
})

export const creditsExhausted = (ctx: TurnErrorContext, message: string, rayId?: string): CreditsExhausted => ({
  _tag: 'CreditsExhausted',
  phase: ctx.phase,
  chatId: ctx.chatId,
  message,
  rayId: rayId ?? RAY_ID_UNAVAILABLE,
})

export const subscriptionRequired = (ctx: TurnErrorContext, message: string, rayId?: string): SubscriptionRequired => ({
  _tag: 'SubscriptionRequired',
  phase: ctx.phase,
  chatId: ctx.chatId,
  message,
  rayId: rayId ?? RAY_ID_UNAVAILABLE,
})

export const cardRequired = (ctx: TurnErrorContext, message: string, rayId?: string): CardRequired => ({
  _tag: 'CardRequired',
  phase: ctx.phase,
  chatId: ctx.chatId,
  message,
  rayId: rayId ?? RAY_ID_UNAVAILABLE,
})

export const onboardingRequired = (ctx: TurnErrorContext, message: string, rayId?: string): OnboardingRequired => ({
  _tag: 'OnboardingRequired',
  phase: ctx.phase,
  chatId: ctx.chatId,
  message,
  rayId: rayId ?? RAY_ID_UNAVAILABLE,
})

export const staleVerifyDropped = (chatId: string, reason: StaleVerifyDropped['reason']): StaleVerifyDropped => ({
  _tag: 'StaleVerifyDropped',
  chatId,
  reason,
})

export const unknownTurnError = (ctx: TurnErrorContext, cause: unknown): UnknownTurnError => ({
  _tag: 'UnknownTurnError',
  phase: ctx.phase,
  chatId: ctx.chatId,
  cause,
})

const TURN_ERROR_TAGS: ReadonlySet<string> = new Set([
  'SseConnectionError',
  'SseDecodeError',
  'BackendTurnError',
  'ArkoseChallengeError',
  'AttachmentUploadError',
  'CreditsExhausted',
  'SubscriptionRequired',
  'CardRequired',
  'OnboardingRequired',
  'StaleVerifyDropped',
  'UnknownTurnError',
])

export function isChatTurnError(value: unknown): value is ChatTurnError {
  return (
    typeof value === 'object' &&
    value !== null &&
    '_tag' in value &&
    typeof (value as { _tag: unknown })._tag === 'string' &&
    TURN_ERROR_TAGS.has((value as { _tag: string })._tag)
  )
}

/**
 * Structural view of the legacy `ChatError` class (chat/types). Duck-typed
 * here instead of imported so the engine stays free of the React-importing
 * types barrel.
 */
type ChatErrorLike = Error & {
  chatId: string
  errorCode: string | null
  rayId?: string
  turnError?: ChatTurnError
}

function isChatErrorLike(value: unknown): value is ChatErrorLike {
  return value instanceof Error && 'chatId' in value && 'errorCode' in value
}

const ATTACHMENT_ERROR_CODES = new Set(['attachment-upload-error', 'file-upload-error', 'file-prepare-error'])

/** Backend `errorCode` for a credit-billed plan with nothing left (gateway `ErrorCodeSchema`). */
export const CREDITS_EXHAUSTED_ERROR_CODE = 'CREDITS_EXHAUSTED'
/** Backend `errorCode` for a 2.0 build with no subscription behind it (gateway `ErrorCodeSchema`). */
export const SUBSCRIPTION_REQUIRED_ERROR_CODE = 'SUBSCRIPTION_REQUIRED'
/** Backend `errorCode` for an account asked to add a card (gateway `ErrorCodeSchema`). */
export const CARD_REQUIRED_ERROR_CODE = 'CARD_REQUIRED'
/** Backend `errorCode` for an account whose 2.0 onboarding was never recorded (gateway `ErrorCodeSchema`). */
export const ONBOARDING_REQUIRED_ERROR_CODE = 'ONBOARDING_REQUIRED'
/** Backend `errorCode` for a turn the gateway's abuse checks refused, Arkose included (gateway `ErrorCodeSchema`). */
export const VERIFICATION_REQUIRED_ERROR_CODE = 'VERIFICATION_REQUIRED'

/**
 * The refusals the user resolves outside the chat (a payment, a plan, the
 * onboarding flow), keyed by backend `errorCode`. The one place that mapping
 * lives, so the submit POST and the legacy `ChatError` unwrap cannot disagree
 * about which codes are an account state rather than a fault.
 */
export function planRefusalFromErrorCode(
  ctx: TurnErrorContext,
  errorCode: string | null | undefined,
  message: string,
  rayId?: string,
): CreditsExhausted | SubscriptionRequired | CardRequired | OnboardingRequired | undefined {
  switch (errorCode) {
    case CREDITS_EXHAUSTED_ERROR_CODE:
      return creditsExhausted(ctx, message, rayId)
    case SUBSCRIPTION_REQUIRED_ERROR_CODE:
      return subscriptionRequired(ctx, message, rayId)
    case CARD_REQUIRED_ERROR_CODE:
      return cardRequired(ctx, message, rayId)
    case ONBOARDING_REQUIRED_ERROR_CODE:
      return onboardingRequired(ctx, message, rayId)
    default:
      return undefined
  }
}

/**
 * Normalizes anything caught at a turn boundary into a `ChatTurnError`.
 * Never call this for interruptions (AbortError) — interruption is a
 * `TurnCompletion`, not an error; callers branch on abort first.
 */
export function toChatTurnError(value: unknown, ctx: TurnErrorContext): ChatTurnError {
  if (isChatTurnError(value)) return value

  if (isChatErrorLike(value)) {
    if (value.turnError) return value.turnError
    const chatId = value.chatId || ctx.chatId
    if (value.errorCode && ATTACHMENT_ERROR_CODES.has(value.errorCode)) {
      return attachmentUploadError(chatId, value.message, value.rayId)
    }
    const planRefusal = planRefusalFromErrorCode(
      { phase: ctx.phase, chatId },
      value.errorCode,
      value.message,
      value.rayId,
    )
    if (planRefusal) return planRefusal
    return backendTurnError(
      { phase: ctx.phase, chatId },
      { kind: 'error-response', message: value.message, errorCode: value.errorCode, rayId: value.rayId },
    )
  }

  return unknownTurnError(ctx, value)
}

/**
 * Whether re-sending the same turn could plausibly succeed, which is what the
 * inline Retry button offers. Sits beside `describeTurnError` rather than at
 * the stamp site so the exhaustive switch makes the compiler ask the question
 * for every new member — retryability is a property of the failure, not of the
 * hook that happens to render it.
 */
export function isRetryable(error: ChatTurnError): boolean {
  switch (error._tag) {
    case 'CreditsExhausted':
    case 'SubscriptionRequired':
    case 'CardRequired':
    case 'OnboardingRequired':
      // Nothing about a spent balance, a missing plan or card, or an unfinished flow
      // changes between attempts, and each one still spends a chat rate-limit
      // token before the gate refuses it.
      return false
    case 'StaleVerifyDropped':
      // Not a turn failure; never stamped onto a message.
      return false
    case 'SseConnectionError':
    case 'SseDecodeError':
    case 'BackendTurnError':
    case 'ArkoseChallengeError':
    case 'AttachmentUploadError':
    case 'UnknownTurnError':
      return true
  }
}

/**
 * User-facing copy for a turn error, derived from its `_tag` — the single
 * place error presentation branches on the taxonomy. Copy matches the
 * pre-taxonomy ERROR_MESSAGES strings so nothing user-visible changed.
 */
export function describeTurnError(error: ChatTurnError): string {
  switch (error._tag) {
    case 'SseConnectionError':
      return error.phase === 'resume'
        ? 'Lost connection to the response stream, check your internet connection and try again.'
        : 'Failed to send request, check your internet connection and try again.'
    case 'SseDecodeError':
      return 'An error occurred while reading the response stream.'
    case 'ArkoseChallengeError':
      switch (error.reason) {
        case 'abandoned':
          return 'Please complete the verification to send your message.'
        case 'failed':
          return "That verification didn't pass. Please try sending your message again."
        case 'cancelled':
          return 'Verification was cancelled.'
        // 'unavailable', 'timeout' and 'error' are our failure or the vendor's,
        // not the user's, so the copy points at retrying.
        default:
          return 'Could not verify your request right now. Please try again.'
      }
    case 'AttachmentUploadError':
      return error.message
    case 'BackendTurnError':
      switch (error.kind) {
        case 'error-response':
          // Resume-side messages are already phrased for the user; the
          // "creating the chat request" prefix only fits the submit POST.
          return error.phase === 'resume'
            ? error.message
            : `An error occurred while creating the chat request: ${error.message}`
        case 'non-json-response':
          return 'An error occurred while sending message.'
        case 'malformed-error-body':
          return 'A validation error occurred while sending the message.'
        case 'stream-error-event':
        case 'unsuccessful-completion':
          return error.message || 'Unknown error'
      }
      break
    case 'CreditsExhausted':
      // The backend already phrases this for the user, and its copy can change
      // without a client release; the fallback only covers an empty body.
      return error.message || "You've used all of your credits for this billing period."
    case 'SubscriptionRequired':
      return error.message || 'Choose a plan to keep chatting in Highlight.'
    case 'CardRequired':
      return error.message || "Add a card to keep using Highlight. You won't be charged today."
    case 'OnboardingRequired':
      return error.message || 'Finish setting up Highlight to start chatting.'
    case 'StaleVerifyDropped':
      return `Dropped a stale streaming-state verify (${error.reason}).`
    case 'UnknownTurnError':
      return `Unknown error occurred while sending message. ${error.cause}`
  }
}

/**
 * Connect to a turn's SSE stream (submit POST / subscribe GET). The one
 * standardized `fromPromise` wrapper for the two fetch call sites — a
 * rejection (network down, DNS, abort-free failure) lands on the Err channel
 * as `SseConnectionError`.
 */
export function connectToTurnStream(
  ctx: TurnErrorContext,
  run: () => Promise<Response>,
): ResultAsync<Response, SseConnectionError> {
  return ResultAsync.fromPromise(run(), (cause) => sseConnectionError(ctx, cause))
}

/**
 * Decode one SSE frame's JSON payload. `fromThrowable` around JSON.parse —
 * a bad frame yields `SseDecodeError` carrying the raw frame instead of a
 * silent skip.
 */
export function decodeTurnFrame(ctx: TurnErrorContext, rawFrame: string): Result<unknown, SseDecodeError> {
  return Result.fromThrowable(
    () => JSON.parse(rawFrame) as unknown,
    () => sseDecodeError(ctx, rawFrame),
  )()
}
